import React from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import Header from '../../components/ui/Header';

const TermsConditions = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-muted-light">
      <Header />
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="bg-card rounded-xl shadow-soft p-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center space-x-3">
              <button
                onClick={() => navigate(-1)}
                className="p-2 rounded-lg bg-muted hover:bg-muted/80 transition-gentle"
              >
                <Icon name="ArrowLeft" size={20} className="text-muted-foreground" />
              </button>
              <h1 className="text-3xl font-heading font-semibold text-foreground">
                Terms and Conditions
              </h1>
            </div>
            <p className="text-sm text-muted-foreground">
              Last updated: {new Date()?.toLocaleDateString()}
            </p>
          </div>

          {/* Content */}
          <div className="prose prose-slate max-w-none">
            <div className="space-y-8">
              
              {/* Introduction */}
              <section>
                <h2 className="text-xl font-heading font-semibold text-foreground mb-4">
                  1. Introduction
                </h2>
                <p className="text-muted-foreground font-body leading-relaxed">
                  Welcome to our holistic wellness directory platform. These Terms and Conditions 
                  ("Terms") govern your use of our website and services. By accessing or using our 
                  platform, you agree to be bound by these Terms.
                </p>
              </section>

              {/* User Accounts */}
              <section>
                <h2 className="text-xl font-heading font-semibold text-foreground mb-4">
                  2. User Accounts and Registration
                </h2>
                <div className="space-y-4">
                  <p className="text-muted-foreground font-body leading-relaxed">
                    To access certain features of our platform, you must create an account. When 
                    creating your account, you agree to:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-muted-foreground font-body ml-4">
                    <li>Provide accurate, current, and complete information</li>
                    <li>Maintain and update your account information</li>
                    <li>Keep your login credentials secure and confidential</li>
                    <li>Accept responsibility for all activities under your account</li>
                    <li>Notify us immediately of any unauthorized account access</li>
                  </ul>
                </div>
              </section>

              {/* User Conduct */}
              <section>
                <h2 className="text-xl font-heading font-semibold text-foreground mb-4">
                  3. Acceptable Use and User Conduct
                </h2>
                <div className="space-y-4">
                  <p className="text-muted-foreground font-body leading-relaxed">
                    You agree to use our platform only for lawful purposes and in accordance with 
                    these Terms. You may not:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-muted-foreground font-body ml-4">
                    <li>Post false, misleading, or fraudulent information</li>
                    <li>Impersonate any person or entity</li>
                    <li>Violate any applicable laws or regulations</li>
                    <li>Interfere with or disrupt the platform or servers</li>
                    <li>Use automated systems to access the platform without permission</li>
                    <li>Collect user information without consent</li>
                  </ul>
                </div>
              </section>

              {/* Business Listings */}
              <section>
                <h2 className="text-xl font-heading font-semibold text-foreground mb-4">
                  4. Business Listings and Content
                </h2>
                <div className="space-y-4">
                  <p className="text-muted-foreground font-body leading-relaxed">
                    If you are a practitioner or business owner listing services on our platform:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-muted-foreground font-body ml-4">
                    <li>You must have the right to offer the services you list</li>
                    <li>All information must be accurate and up-to-date</li>
                    <li>You are responsible for the content of your listings</li>
                    <li>You must comply with all applicable professional standards and regulations</li>
                    <li>You grant us permission to display your content on our platform</li>
                  </ul>
                </div>
              </section>

              {/* Booking and Appointments */}
              <section>
                <h2 className="text-xl font-heading font-semibold text-foreground mb-4">
                  5. Booking and Appointments
                </h2>
                <p className="text-muted-foreground font-body leading-relaxed">
                  Our platform facilitates connections between clients and practitioners. We are not 
                  responsible for the actual services provided. All booking confirmations, 
                  cancellations, and service delivery are between you and the practitioner directly.
                </p>
              </section>

              {/* Payment */}
              <section>
                <h2 className="text-xl font-heading font-semibold text-foreground mb-4">
                  6. Payment and Fees
                </h2>
                <p className="text-muted-foreground font-body leading-relaxed">
                  Payment for services is handled directly between clients and practitioners. 
                  We may charge fees for premium listing features or enhanced services, which 
                  will be clearly disclosed before purchase.
                </p>
              </section>

              {/* Intellectual Property */}
              <section>
                <h2 className="text-xl font-heading font-semibold text-foreground mb-4">
                  7. Intellectual Property
                </h2>
                <p className="text-muted-foreground font-body leading-relaxed">
                  All content on our platform, including design, text, graphics, logos, and software, 
                  is owned by us or our licensors and protected by copyright and other intellectual 
                  property laws. You may not copy, modify, or distribute our content without permission.
                </p>
              </section>

              {/* Privacy */}
              <section>
                <h2 className="text-xl font-heading font-semibold text-foreground mb-4">
                  8. Privacy and Data Protection
                </h2>
                <p className="text-muted-foreground font-body leading-relaxed">
                  Your privacy is important to us. Please review our Privacy Policy to understand 
                  how we collect, use, and protect your personal information. By using our platform, 
                  you consent to our privacy practices.
                </p>
              </section>

              {/* Disclaimers */}
              <section>
                <h2 className="text-xl font-heading font-semibold text-foreground mb-4">
                  9. Disclaimers and Limitations
                </h2>
                <div className="space-y-4">
                  <p className="text-muted-foreground font-body leading-relaxed">
                    Our platform is provided "as is" without warranties of any kind. We disclaim 
                    all warranties, express or implied, including:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-muted-foreground font-body ml-4">
                    <li>The accuracy or completeness of listings or content</li>
                    <li>The quality or safety of services provided by practitioners</li>
                    <li>Uninterrupted or error-free operation of the platform</li>
                    <li>The security of data transmission or storage</li>
                  </ul>
                </div>
              </section>

              {/* Termination */}
              <section>
                <h2 className="text-xl font-heading font-semibold text-foreground mb-4">
                  10. Account Termination
                </h2>
                <p className="text-muted-foreground font-body leading-relaxed">
                  We may suspend or terminate your account at any time for violation of these Terms 
                  or for any other reason. You may also delete your account at any time through 
                  your account settings.
                </p>
              </section>

              {/* Changes to Terms */}
              <section>
                <h2 className="text-xl font-heading font-semibold text-foreground mb-4">
                  11. Changes to Terms
                </h2>
                <p className="text-muted-foreground font-body leading-relaxed">
                  We reserve the right to modify these Terms at any time. We will notify users of 
                  significant changes via email or platform notifications. Continued use of our 
                  platform after changes constitutes acceptance of the new Terms.
                </p>
              </section>

              {/* Contact */}
              <section>
                <h2 className="text-xl font-heading font-semibold text-foreground mb-4">
                  12. Contact Information
                </h2>
                <p className="text-muted-foreground font-body leading-relaxed">
                  If you have questions about these Terms and Conditions, please contact us at:
                </p>
                <div className="mt-4 p-4 bg-muted rounded-lg">
                  <p className="text-muted-foreground font-body">
                    <strong>Email:</strong> legal@holisticdirectory.com<br/>
                    <strong>Address:</strong> Legal Department<br/>
                    Holistic Wellness Directory<br/>
                    123 Wellness Street<br/>
                    London, UK
                  </p>
                </div>
              </section>

            </div>
          </div>

          {/* Footer Actions */}
          <div className="mt-12 pt-8 border-t border-border">
            <div className="flex flex-col sm:flex-row gap-4 justify-between items-center">
              <Button
                variant="outline"
                onClick={() => navigate(-1)}
                iconName="ArrowLeft"
                iconPosition="left"
              >
                Go Back
              </Button>
              <Button
                variant="default"
                onClick={() => navigate('/register')}
                iconName="UserPlus"
                iconPosition="left"
              >
                Create Account
              </Button>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default TermsConditions;